import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { User } from '../types';

type EventKey =
  | 'new_order'
  | 'store_cart_confirmed'
  | 'cart_not_confirmed'
  | 'signup_not_confirmed'
  | 'order_items_not_purchased_reminder'
  | 'order_stuck_in_items_purchased'
  | 'invoice_not_exported_reminder';

const EVENTS: { key: EventKey; label: string }[] = [
  { key: 'new_order', label: 'New Orders' },
  { key: 'store_cart_confirmed', label: 'Customer Confirmed Cart' },
  { key: 'cart_not_confirmed', label: 'Cart Not Confirmed' },
  { key: 'signup_not_confirmed', label: 'Signup Not Confirmed' },
  { key: 'order_items_not_purchased_reminder', label: 'Items Not Purchased' },
  { key: 'order_stuck_in_items_purchased', label: 'Stuck in Items Purchased' },
  { key: 'invoice_not_exported_reminder', label: 'Invoice Not Exported' },
];

type PreferencesState = Record<string, { production: boolean; test: boolean }>;

const buildInitialPreferences = (
  prefs: User['notification_preferences']
): PreferencesState => {
  const result: PreferencesState = {};
  EVENTS.forEach(({ key }) => {
    result[key] = {
      production: Boolean(prefs?.[key]?.production),
      test: Boolean(prefs?.[key]?.test),
    };
  });
  return result;
};

const NotificationPreferences = () => {
  const [user, setUser] = useState<User | null>(null);
  const [preferences, setPreferences] = useState<PreferencesState>(
    buildInitialPreferences(undefined)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchMe = async () => {
      setIsLoading(true);
      try {
        const res = await fetchWithAuth('/users/me');
        if (!res.ok) return;
        const data = await res.json();
        setUser(data.user);
        setPreferences(buildInitialPreferences(data.user.notification_preferences));
      } finally {
        setIsLoading(false);
      }
    };
    fetchMe();
  }, []);

  const togglePreference = (event: EventKey) => {
    setPreferences((prev) => {
      const isEnabled = prev[event].production || prev[event].test;
      const next = !isEnabled;
      return {
        ...prev,
        [event]: { production: next, test: next },
      };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await fetchWithAuth('/users/me', {
        method: 'PATCH',
        body: JSON.stringify({ notification_preferences: preferences }),
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setPreferences(buildInitialPreferences(data.user.notification_preferences));
        toast.success('Notification preferences saved');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Notification Preferences</h1>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Slack User ID
        </label>
        {user?.slack_user_id ? (
          <p className="text-gray-800">{user.slack_user_id}</p>
        ) : (
          <p className="text-sm text-gray-500">Not set. Ask an admin to add it on the Users page.</p>
        )}
      </div>

      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">Slack notifications</h2>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Event</th>
              <th className="text-center py-2 px-3 text-sm font-medium text-gray-700">
                Enabled
              </th>
            </tr>
          </thead>
          <tbody>
            {EVENTS.map(({ key: eventKey, label }) => (
              <tr key={eventKey} className="border-b">
                <td className="py-3 px-3 text-gray-800">{label}</td>
                <td className="py-3 px-3 text-center">
                  <input
                    type="checkbox"
                    aria-label={`${label} enabled`}
                    className="h-4 w-4"
                    checked={
                      preferences[eventKey].production || preferences[eventKey].test
                    }
                    onChange={() => togglePreference(eventKey)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving}
        className="bg-black text-white rounded-md py-2 px-4 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
};

export default NotificationPreferences;
